import { View, Text } from '@react-pdf/renderer';
import { styles } from './PDFStyles';
import { getPlanColor } from '@/utils/colors';
import type { SimplifiedPlan } from '@/utils/previewFormatters';
import { formatCurrency, type CurrencyCode } from '@/utils/currencyFormatter';

interface PDFPlanLegendProps {
  plans: SimplifiedPlan[];
  filterOptions: {
    showPlanDescription: boolean;
    showPlanMonthlyPrice: boolean;
    showPlanYearlyPrice: boolean;
  };
  currency: CurrencyCode;
}

export function PDFPlanLegend({ plans, filterOptions, currency }: PDFPlanLegendProps) {
  // Validate and filter plans
  const validPlans = Array.isArray(plans) ? plans.filter(plan => plan && plan.id && plan.name) : [];
  
  if (validPlans.length === 0) return null;
  
  const { showPlanDescription, showPlanMonthlyPrice, showPlanYearlyPrice } = filterOptions;
  
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Plan Legend</Text>
      <View style={styles.table}>
        <View style={[styles.tableRow, styles.tableHeader]}>
          <View style={[styles.tableCell, { flex: 1 }]}>
            <Text>Color</Text>
          </View>
          <View style={[styles.tableCell, { flex: 3 }]}>
            <Text>Plan Name</Text>
          </View>
          {showPlanMonthlyPrice && (
            <View style={[styles.tableCell, { flex: 2 }]}>
              <Text>Monthly Price</Text>
            </View>
          )}
          {showPlanYearlyPrice && (
            <View style={[styles.tableCell, { flex: 2 }]}>
              <Text>Yearly Price</Text>
            </View>
          )}
          {showPlanDescription && (
            <View style={[styles.tableCell, { flex: 4 }]}>
              <Text>Description</Text>
            </View>
          )}
          <View style={[styles.tableCell, { flex: 1 }]}>
            <Text>Channels</Text>
          </View>
        </View>
        
        {validPlans.map(plan => (
          <View key={plan.id} style={styles.tableRow}>
            <View style={[styles.tableCell, { flex: 1 }]}>
              <View 
                style={[
                  styles.planIndicator,
                  { 
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: getPlanColor(plan.color_index).light,
                    borderColor: getPlanColor(plan.color_index).dark,
                    borderWidth: 1
                  }
                ]} 
              />
            </View>
            <View style={[styles.tableCell, { flex: 3 }]}>
              <Text>{plan.name || ''}</Text>
            </View>
            {showPlanMonthlyPrice && (
              <View style={[styles.tableCell, { flex: 2 }]}>
                <Text>{formatCurrency(plan.monthly_price, currency)}</Text>
              </View>
            )}
            {showPlanYearlyPrice && (
              <View style={[styles.tableCell, { flex: 2 }]}>
                <Text>{formatCurrency(plan.yearly_price, currency)}</Text>
              </View>
            )}
            {showPlanDescription && (
              <View style={[styles.tableCell, { flex: 4 }]}>
                <Text style={{ flexWrap: 'wrap' }}>{plan.description || "-"}</Text>
              </View>
            )}
            <View style={[styles.tableCell, { flex: 1 }]}>
              <Text>{(plan.channel_count || 0).toString()}</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
} 